import roommateSignupAction from 'client/actions/Auth/roommateSignup';
import getListingByIdAction from 'client/actions/Listing/getById';
import startApplicationAction from 'client/actions/Household/startApplication';
import getApplicationsAction from 'client/actions/Household/getApplications';
import loadRenterProfileAction from 'client/actions/Household/loadRenterProfile';

import { LISTING_DATA_SOURCE_SUNROOM } from 'common/listingConstants';

import {
  SCREEN_ID_HOUSEHOLD_INVITE_GUARANTOR,
  SCREEN_ID_HOUSEHOLD_INVITE_GUARANTOR_SENT,
  SCREEN_ID_HOUSEHOLD_COLLECT_ROOMMATE_COUNT,
  SCREEN_ID_HOUSEHOLD_COLLECT_ROOMMATE_INFO,
  SCREEN_ID_HOUSEHOLD_ROOMMATE_INVITES_SENT,
  SCREEN_ID_HOUSEHOLD_CONFIRM_IN_PERSON_TOUR,
} from 'client/constants/screenIds';

import {
  SR_API_ACTION_ROOMMATE_INVITES,
  SR_API_ACTION_HOUSEHOLD_GET_BY_USER_ID,
  SR_API_ACTION_HOUSEHOLD_MARK_COMPLETED_ROOMMATE_FLOW,
  SR_API_ACTION_HOUSEHOLD_LISTING_GET_BY_HOUSEHOLD_AND_LISTING_ID,
  SR_API_ACTION_LISTING_WANTS_TO_APPLY,
  SR_API_ACTION_SEND_APPLICATION_EMAIL,
  SR_API_ACTION_PRE_LEASE_GET_BY_LISTING_ID,
} from 'api/actionTypes';

import { isListingTenantScreening } from 'common/listingHelpers';

import { hasRoleAnonymous } from 'common/userRoles';
import { isPreLeaseActive } from 'common/preLeaseHelpers';

export default async function roommateApplicationStep(
  { state, history, apiClient },
  { listingId, roommatePayload },
) {
  const currentUser = state.get(['currentUser']);
  const screenId = state.get(['analytics', 'screenId']);

  if (hasRoleAnonymous(currentUser.roles)) {
    history.push(`/login?wantsToApply=true&listingId=${listingId}`);
    return;
  }

  const fetchDataResult = await Promise.all([
    getListingByIdAction({ state, apiClient }, listingId),
    getApplicationsAction(
      { state, apiClient },
      {
        userId: currentUser.id,
      },
    ),
    apiClient.post(SR_API_ACTION_HOUSEHOLD_GET_BY_USER_ID, {
      userId: currentUser.id,
    }),
    loadRenterProfileAction(
      { state, apiClient },
      {
        userId: currentUser.id,
      },
    ),
  ]);

  const [listing] = fetchDataResult;
  let [, applications, currentHousehold] = fetchDataResult;
  const renterProfile = fetchDataResult[3];

  const listingTenantScreening = isListingTenantScreening(listing);

  let application = (applications || []).find((application) => application.listingId === listingId);

  if (!application) {
    await apiClient.post(SR_API_ACTION_LISTING_WANTS_TO_APPLY, {
      userId: currentUser.id,
      listingId: listingId,
      wantsToApply: true,
    });

    applications = await getApplicationsAction(
      { state, apiClient },
      {
        userId: currentUser.id,
      },
    );

    application = (applications || []).find((application) => application.listingId === listingId);

    currentHousehold = await apiClient.post(SR_API_ACTION_HOUSEHOLD_GET_BY_USER_ID, {
      userId: currentUser.id,
    });
  }

  const isLeadRenter = currentUser.id === currentHousehold.leadRenterId;

  const onRoommateFlowCompleted = async () => {
    const leadRenter = state.get(['currentUser']);

    if (listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM) {
      await apiClient.post(SR_API_ACTION_SEND_APPLICATION_EMAIL, {
        userId: leadRenter.id,
        listingId: listing.id,
      });
    }

    await apiClient.post(SR_API_ACTION_HOUSEHOLD_MARK_COMPLETED_ROOMMATE_FLOW, {
      householdId: currentHousehold.id,
    });
  };

  if (currentHousehold.completedRoommateFlow && application.guarantorInvited) {
    await startApplicationAction({ state, history, apiClient }, { listingId });
  } else if (screenId === SCREEN_ID_HOUSEHOLD_COLLECT_ROOMMATE_COUNT) {
    state.set(['roommateApplication', 'roommates'], []);
    state.set(['roommateApplication', 'count'], roommatePayload.count);
    //  If the user is living without roommates, we don't need to collect roommate information.
    if (roommatePayload.count === 0 || !isLeadRenter) {
      history.push(`/application/${currentUser.id}/${listingId}/invite-guarantor`);
    } else {
      history.push(`/application/${currentUser.id}/${listingId}/roommate-info`);
    }
  }
  // Sign-up all the roommates prodived by this step.
  else if (screenId === SCREEN_ID_HOUSEHOLD_COLLECT_ROOMMATE_INFO) {
    state.set(['roommateApplication', 'roommates'], roommatePayload.roommates);
    const leadRenter = state.get(['currentUser']);
    const usersToBeCreated = state.get(['roommateApplication', 'roommates']);
    for (const user of usersToBeCreated) {
      await roommateSignupAction(
        { state, apiClient },
        {
          name: user.name,
          phone: user.phone,
          email: user.email,
          password: user.phone,
          originalRenterPhone: leadRenter.phone,
          skipLogin: true,
        },
      );
    }
    await apiClient.post(SR_API_ACTION_ROOMMATE_INVITES, {
      userId: leadRenter.id,
      listingId: listing.id,
      roommatesInvited: usersToBeCreated,
    });

    await onRoommateFlowCompleted();
    history.push(`/application/${currentUser.id}/${listing.id}/invites`);
  } else if (screenId === SCREEN_ID_HOUSEHOLD_ROOMMATE_INVITES_SENT) {
    if (listingTenantScreening && renterProfile.guarantorCount === 0) {
      await startApplicationAction({ state, history, apiClient }, { listingId });
      return;
    }

    history.push(`/application/${currentUser.id}/${listingId}/invite-guarantor`);
  } else if (
    screenId === SCREEN_ID_HOUSEHOLD_INVITE_GUARANTOR ||
    screenId === SCREEN_ID_HOUSEHOLD_INVITE_GUARANTOR_SENT
  ) {
    await onRoommateFlowCompleted();
    await startApplicationAction({ state, history, apiClient }, { listingId });
  } else if (screenId === SCREEN_ID_HOUSEHOLD_CONFIRM_IN_PERSON_TOUR) {
    if (!isLeadRenter) {
      history.push(`/application/${currentUser.id}/${listingId}/invite-guarantor`);
      return;
    }
    history.push(`/application/${currentUser.id}/${listingId}/roommate-count`);
  } else {
    const householdListing = await apiClient.post(
      SR_API_ACTION_HOUSEHOLD_LISTING_GET_BY_HOUSEHOLD_AND_LISTING_ID,
      {
        householdId: currentHousehold.id,
        listingId: listingId,
      },
    );

    if (listingTenantScreening) {
      if (
        renterProfile.prequalRoommateCount > 0 &&
        !currentHousehold.completedRoommateFlow &&
        isLeadRenter
      ) {
        state.set(['roommateApplication', 'roommates'], []);
        state.set(['roommateApplication', 'count'], renterProfile.prequalRoommateCount);
        history.push(`/application/${currentUser.id}/${listingId}/roommate-info`);
      } else if (
        renterProfile.guarantorCount > 0 &&
        !application.guarantorInvited &&
        isLeadRenter
      ) {
        history.push(`/application/${currentUser.id}/${listingId}/invite-guarantor`);
      } else {
        await startApplicationAction({ state, history, apiClient }, { listingId });
      }
      return;
    }

    const preLease = await apiClient.post(SR_API_ACTION_PRE_LEASE_GET_BY_LISTING_ID, { listingId });
    const isPreLease = preLease && isPreLeaseActive({ preLease });

    if (householdListing.inPersonTour || isPreLease) {
      if (!isLeadRenter) {
        history.push(`/application/${currentUser.id}/${listingId}/invite-guarantor`);
        return;
      }
      history.push(`/application/${currentUser.id}/${listingId}/roommate-count`);
    } else {
      history.push(`/application/${currentUser.id}/${listingId}/in-person-tour`);
    }
  }

  state.commit();
}
